import React, { useState, useEffect, useRef } from 'react';
import { ConfigProvider } from 'antd';
import daDA from 'antd/locale/da_DK';
import './App.css';

// Hooks
import { useSimulationEngine } from './hooks/useSimulationEngine';

// Context
import { ScenarioProvider, useScenarios } from './contexts/ScenarioContext';

// Theme
import { ANT_THEME } from './constants/theme';

// Utils
import { createScaledDataProvider, validateAnnualDemand } from './utils/demandScaler';
import { loadCSVFromPublic, createDemandProfile, createCSVDataProvider } from './utils/csvDataLoader';
import { SCENARIO_TEMPLATES } from './config/scenarioTemplates';

// Components
import SimulationControls from './components/SimulationControls';
import FinancialDashboard from './components/FinancialDashboard';
import OperationalDashboard from './components/OperationalDashboard';
import SystemServicesCard from './components/SystemServicesCard';
import ConfigurationPanel from './components/ConfigurationPanel';
import SankeyDiagramSimple from './components/SankeyDiagramSimple';
import ScenarioSelector from './components/layout/ScenarioSelector';
import ScenarioConfigurator from './components/ScenarioConfigurator';
import DataInsights from './components/DataInsights';

// Constants
import { COLORS } from './constants/theme';

// Utils
import { exportCompleteFinancialReport } from './utils/excelExporter';

/**
 * Main Application Component
 * Fjernvarme Skælskør Energy Simulation
 * Clean, modular architecture with <1500 lines
 */
function App() {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState('');
  
  // Get active scenario from context
  const { getActiveScenario, createScenario } = useScenarios();
  const activeScenario = getActiveScenario();

  // Configuration state - initialize from active scenario or default
  const [config, setConfig] = useState(() => {
    if (activeScenario && activeScenario.config) {
      return activeScenario.config;
    }
    // Default to Skælskør Full template
    return SCENARIO_TEMPLATES.SKAELSKOER_FULL.config;
  });

  // Update config when active scenario changes
  useEffect(() => {
    if (activeScenario && activeScenario.config) {
      setConfig(activeScenario.config);
    }
  }, [activeScenario]);
  
  const [showConfig, setShowConfig] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [showScenarioConfig, setShowScenarioConfig] = useState(false);
  
  // Data loading state
  const [priceData, setPriceData] = useState(null);
  const [solarData, setSolarData] = useState(null);
  const [wasteHeatData, setWasteHeatData] = useState(null);
  const [demandData, setDemandData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  
  // Simulation engine hook
  const {
    isRunning,
    isPaused,
    currentHour,
    hourlyData,
    stats,
    speed,
    mode,
    validationResult,
    cumulativeFlows,
    startSimulation,
    pauseSimulation,
    resumeSimulation,
    resetSimulation,
    setSpeed,
    setMode,
    setDataProvider,
    engine,
  } = useSimulationEngine(config);
  
  // Debug: Log stats when they change
  useEffect(() => {
    if (stats && Object.keys(stats).length > 0) {
      console.log('📊 Full stats object:', stats);
      console.log('📊 Key values:', {
        operationalGridCost: stats.operationalGridCost,
        operationalSolarCost: stats.operationalSolarCost,
        wasteHeatAvailable: stats.wasteHeatAvailable,
        wasteHeatAccepted: stats.wasteHeatAccepted,
        avgGridPrice: stats.avgGridPrice,
        avgSolarPrice: stats.avgSolarPrice,
        netDelivery: stats.netDelivery
      });
    }
  }, [stats]);
  
  // Load data from CSV or JSON files
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);

        // Try to load CSV data first (new format)
        try {
          console.log('🔍 Trying to load CSV spot price data...');
          const csvData = await loadCSVFromPublic('Elpriser-2020-2025-apri-kundk2l.csv');

          // Extract prices from CSV
          const prices = csvData.map(d => d.spotPrice);

          // For CSV mode, use flat demand profile or monthly distribution from config
          // Solar and waste heat are disabled for CSV mode (grid-only scenarios)
          const solar = new Array(8784).fill(0);
          const waste = new Array(8784).fill(0);
          const demand = new Array(8784).fill(5); // Default flat 5 MW

          console.log('✅ CSV data loaded:', {
            prices: prices.length,
            priceRange: `${Math.min(...prices).toFixed(2)} - ${Math.max(...prices).toFixed(2)} DKK/MWh`,
            avgPrice: (prices.reduce((sum, p) => sum + p, 0) / prices.length).toFixed(2)
          });

          setPriceData(prices);
          setSolarData(solar);
          setWasteHeatData(waste);
          setDemandData(demand);
          setIsLoading(false);
          console.log('🎯 CSV data loading complete');
          return;
        } catch (csvError) {
          console.log('⚠️ CSV load failed, falling back to JSON:', csvError.message);
        }

        // Fallback to JSON data (old format)
        console.log('🔍 Loading from /simulationData2024.json');
        const response = await fetch('/simulationData2024.json');

        if (!response.ok) {
          throw new Error('Failed to load simulation data');
        }

        const data = await response.json();
        console.log('📦 JSON data loaded:', {
          totalHours: data.hourlyData?.length,
          firstHour: data.hourlyData?.[0],
          lastHour: data.hourlyData?.[data.hourlyData?.length - 1]
        });

        // Extract data from hourlyData array
        const prices = new Array(8784).fill(0);
        const solar = new Array(8784).fill(0);
        const waste = new Array(8784).fill(0);
        const demand = new Array(8784).fill(0);

        data.hourlyData.forEach(hour => {
          const idx = hour.hourIndex;
          prices[idx] = hour.spotPrice;
          solar[idx] = hour.solarMWh;
          waste[idx] = hour.wasteHeatMW;
          demand[idx] = hour.demandMW;
        });

        console.log('✅ JSON data arrays created');

        setPriceData(prices);
        setSolarData(solar);
        setWasteHeatData(waste);
        setDemandData(demand);
        setIsLoading(false);
        console.log('🎯 JSON data loading complete');
      } catch (error) {
        console.error('❌ Error loading data:', error);
        setLoadError(error.message);
        setIsLoading(false);
      }
    };

    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated]);
  
  // Initialize simulation engine with data
  useEffect(() => {
    console.log('📊 Data check - priceData:', priceData?.length, 'solarData:', solarData?.length,
                'wasteHeatData:', wasteHeatData?.length, 'demandData:', demandData?.length);

    if (priceData && solarData && wasteHeatData && demandData && config) {
      console.log('✅ All data arrays loaded, creating scaled data provider');

      // Create base data provider with real 2024 data
      const baseDataProvider = {
        getSpotPrice: (hour) => priceData[hour] || 0,
        getSolarMW: (hour) => solarData[hour] || 0,
        getWasteHeatMW: (hour) => wasteHeatData[hour] || 0,
        getDemandMW: (hour) => demandData[hour] || 0,
      };

      // Create scaled data provider based on scenario config
      const scaledProvider = createScaledDataProvider(baseDataProvider, config);

      // VALIDATE An-værk demand (now scaled UP from NET by 1/0.90)
      // config.annualDemandGWh is NET target (e.g. 15 GWh)
      // scaledProvider returns An-værk demand = NET / 0.90 = 16.67 GWh
      const validation = validateAnnualDemand(scaledProvider, config.annualDemandGWh / 0.90);
      const anVaerkDemandGWh = validation.actualGWh; // This is An-værk demand
      const expectedNET = anVaerkDemandGWh * 0.90; // Expected NET after grid loss

      console.log('📈 Demand validation:');
      console.log(`   NET target (config): ${config.annualDemandGWh.toFixed(2)} GWh`);
      console.log(`   An-værk demand (scaled 1/0.90): ${anVaerkDemandGWh.toFixed(2)} GWh`);
      console.log(`   Expected NET after grid loss: ${expectedNET.toFixed(2)} GWh`);
      console.log(`   Deviation: ${(expectedNET - config.annualDemandGWh).toFixed(3)} GWh (${((expectedNET / config.annualDemandGWh - 1) * 100).toFixed(2)}%)`);

      if (Math.abs(expectedNET - config.annualDemandGWh) > config.annualDemandGWh * 0.01) {
        console.warn(`⚠️ WARNING: NET delivery will deviate >1% from target!`);
      } else {
        console.log('✅ Demand scaling KORREKT - vil levere target NET ±1%');
      }

      console.log('');
      console.log('🎯 Forventet leveringskæde:');
      console.log(`   1. NET target (kunde): ${config.annualDemandGWh.toFixed(2)} GWh`);
      console.log(`   2. ← Grid loss 10%`);
      console.log(`   3. Ab-værk output: ${(config.annualDemandGWh / 0.90).toFixed(2)} GWh`);
      console.log(`   4. ← No loss between plants`);
      console.log(`   5. An-værk demand: ${anVaerkDemandGWh.toFixed(2)} GWh ← SIMULATION TARGET`);
      console.log(`   6. ← LTES discharge (no loss)`);
      console.log(`   7. LTES charge needed: ${anVaerkDemandGWh.toFixed(2)} GWh`);
      console.log(`   8. ← Kedel efficiency 95%`);
      console.log(`   9. El input needed: ${(anVaerkDemandGWh / 0.95).toFixed(2)} GWh`);

      console.log('📤 Calling setDataProvider with scaled provider');
      console.log('   - Energy sources:', config.energySources);
      console.log('   - Annual demand:', config.annualDemandGWh, 'GWh');
      console.log('   - Monthly distribution:', config.monthlyDistribution ? 'Yes' : 'No (using real data)');

      setDataProvider(scaledProvider);
    } else {
      console.log('⏳ Waiting for all data to load...');
    }
  }, [priceData, solarData, wasteHeatData, demandData, config, setDataProvider]);
  
  // Handle authentication
  const handleLogin = (username, password) => {
    if (username === 'CEO' && password === 'fdskaelskoer!1') {
      setIsAuthenticated(true);
      setAuthError('');
    } else {
      setAuthError('Forkert brugernavn eller adgangskode');
    }
  };
  
  // Export functions
  const exportResults = () => {
    try {
      const exportData = {
        metadata: {
          exportDate: new Date().toISOString(),
          simulationHours: currentHour,
          configuration: config,
        },
        summary: stats,
        hourlyData: hourlyData,
        validation: validationResult,
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `simulation-results-${new Date().toISOString()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export fejlede: ' + error.message);
    }
  };
  
  const exportFinancialReport = () => {
    try {
      exportCompleteFinancialReport(stats, config, hourlyData || []);
    } catch (error) {
      console.error('Financial export failed:', error);
      alert('Finansiel export fejlede: ' + error.message);
    }
  };
  
  // Render login form if not authenticated
  if (!isAuthenticated) {
    return <LoginForm onLogin={handleLogin} error={authError} />;
  }
  
  // Render loading state
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: COLORS.ui.background,
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="loading-spinner" />
          <p style={{ color: COLORS.ui.text.primary, marginTop: '1rem' }}>
            Indlæser simuleringsdata...
          </p>
        </div>
      </div>
    );
  }
  
  // Render error state
  if (loadError) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: COLORS.ui.background,
      }}>
        <div style={{
          background: COLORS.ui.surface,
          padding: '2rem',
          borderRadius: '1rem',
          border: `1px solid ${COLORS.status.error}`,
        }}>
          <h2 style={{ color: COLORS.status.error, marginBottom: '1rem' }}>
            Fejl ved indlæsning af data
          </h2>
          <p style={{ color: COLORS.ui.text.secondary }}>{loadError}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              background: COLORS.status.info,
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
            }}
          >
            Prøv igen
          </button>
        </div>
      </div>
    );
  }
  
  // Main application render
  return (
    <div className="app-container" style={{ background: COLORS.ui.background }}>
      {/* Header */}
      <header style={{
        padding: '1.5rem',
        marginBottom: '2rem',
        background: COLORS.ui.surface,
        borderBottom: `1px solid ${COLORS.ui.border}`,
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
        }}>
          <div>
            <h1 style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              background: `linear-gradient(135deg, ${COLORS.status.info}, ${COLORS.operational.heater})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '0.25rem',
            }}>
              Simulering case - Skælskør på 2024 data
            </h1>
            <p style={{ color: COLORS.ui.text.secondary }}>
              Avanceret energisimulering med HTES/LTES lagersystemer
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <button
              onClick={() => setShowConfig(!showConfig)}
              style={{
                padding: '0.5rem 1rem',
                background: showConfig ? COLORS.status.info : COLORS.ui.border,
                color: COLORS.ui.text.primary,
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer',
              }}
            >
              ⚙️ Konfiguration
            </button>
            
            <button
              onClick={exportResults}
              disabled={!hourlyData.length}
              style={{
                padding: '0.5rem 1rem',
                background: hourlyData.length ? COLORS.status.success : COLORS.ui.border,
                color: COLORS.ui.text.primary,
                border: 'none',
                borderRadius: '0.5rem',
                cursor: hourlyData.length ? 'pointer' : 'not-allowed',
                opacity: hourlyData.length ? 1 : 0.5,
              }}
            >
              📥 Eksporter
            </button>
            
            <button
              onClick={exportFinancialReport}
              disabled={!stats.netDelivery}
              style={{
                padding: '0.5rem 1rem',
                background: stats.netDelivery ? COLORS.financial.profit : COLORS.ui.border,
                color: COLORS.ui.text.primary,
                border: 'none',
                borderRadius: '0.5rem',
                cursor: stats.netDelivery ? 'pointer' : 'not-allowed',
                opacity: stats.netDelivery ? 1 : 0.5,
              }}
            >
              📊 Finansiel Rapport
            </button>
          </div>
        </div>
        
        {/* Scenario Selector */}
        <div style={{ marginTop: '1rem' }}>
          <ScenarioSelector />
        </div>
      </header>
      
      <div style={{ padding: '0 1.5rem 2rem' }}>
        {/* Configuration Panel */}
        {showConfig && (
          <div style={{ marginBottom: '2rem' }}>
            <ConfigurationPanel
              config={config}
              onConfigChange={setConfig}
              onClose={() => setShowConfig(false)}
            />
          </div>
        )}
        
        {/* Scenario Configuration Button */}
        <div style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <button
            onClick={() => setShowScenarioConfig(true)}
            style={{
              padding: '0.75rem 1.5rem',
              background: COLORS.status.info,
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold',
            }}
          >
            Skift Scenario
          </button>
          <span style={{ color: COLORS.ui.text.secondary }}>
            Nuværende: {config.annualDemandGWh} GWh | {config.heater_MW} MW kedel |{' '}
            {config.energySources?.solar ? 'Alle kilder' : 'Kun elnet'}
          </span>
        </div>

        {/* Scenario Configurator Modal */}
        <ScenarioConfigurator
          visible={showScenarioConfig}
          onClose={() => setShowScenarioConfig(false)}
          onSave={(newConfig) => {
            setConfig(newConfig);
            console.log('🔄 Scenario config updated:', newConfig);
          }}
          initialConfig={config}
        />

        {/* Simulation Controls */}
        <SimulationControls
          isRunning={isRunning}
          isPaused={isPaused}
          currentHour={currentHour}
          speed={speed}
          mode={mode}
          onStart={startSimulation}
          onPause={pauseSimulation}
          onResume={resumeSimulation}
          onReset={resetSimulation}
          onSpeedChange={setSpeed}
          onModeChange={setMode}
          config={config}
          onConfigChange={setConfig}
        />
        
        {/* Operational Dashboard */}
        <OperationalDashboard
          stats={stats}
          config={config}
          currentHour={currentHour}
        />
        
        {/* Financial Dashboard */}
        <FinancialDashboard
          stats={stats}
          config={config}
        />

        {/* Data Insights */}
        {priceData && priceData.length > 0 && (
          <div style={{ marginTop: '2rem' }}>
            <DataInsights priceData={priceData} />
          </div>
        )}

        {/* Sankey Diagram */}
        <div style={{
          background: COLORS.ui.surface,
          borderRadius: '0.75rem',
          padding: '1.5rem',
          marginTop: '2rem',
          border: `1px solid ${COLORS.ui.border}`,
        }}>
          <h2 style={{
            color: COLORS.ui.text.primary,
            marginBottom: '1rem',
            fontSize: '1.5rem',
            borderBottom: `2px solid ${COLORS.status.info}`,
            paddingBottom: '0.5rem',
          }}>
            Energiflow Visualisering
          </h2>
          <SankeyDiagramSimple
            hourlyData={hourlyData}
            currentHour={currentHour}
            stats={stats}
            cumulativeFlows={cumulativeFlows}
            config={config}
          />
        </div>
        
        {/* Validation Results */}
        {validationResult && currentHour >= 8783 && (
          <ValidationModal
            result={validationResult}
            onClose={() => setShowValidation(false)}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Login Form Component
 */
function LoginForm({ onLogin, error }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onLogin(username, password);
  };
  
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      background: `linear-gradient(135deg, ${COLORS.ui.background}, ${COLORS.ui.surface})`,
    }}>
      <form
        onSubmit={handleSubmit}
        style={{
          background: COLORS.ui.surface,
          padding: '2rem',
          borderRadius: '1rem',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
          width: '100%',
          maxWidth: '400px',
          border: `1px solid ${COLORS.ui.border}`,
        }}
      >
        <h2 style={{
          textAlign: 'center',
          marginBottom: '2rem',
          background: `linear-gradient(135deg, ${COLORS.status.info}, ${COLORS.operational.heater})`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          Fjernvarme Skælskør
        </h2>
        
        <div style={{ marginBottom: '1rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            color: COLORS.ui.text.secondary,
            fontSize: '0.875rem',
          }}>
            Brugernavn
          </label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: COLORS.ui.background,
              border: `1px solid ${COLORS.ui.border}`,
              borderRadius: '0.5rem',
              color: COLORS.ui.text.primary,
              fontSize: '1rem',
            }}
            required
          />
        </div>
        
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            color: COLORS.ui.text.secondary,
            fontSize: '0.875rem',
          }}>
            Adgangskode
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: COLORS.ui.background,
              border: `1px solid ${COLORS.ui.border}`,
              borderRadius: '0.5rem',
              color: COLORS.ui.text.primary,
              fontSize: '1rem',
            }}
            required
          />
        </div>
        
        {error && (
          <div style={{
            marginBottom: '1rem',
            padding: '0.75rem',
            background: `${COLORS.status.error}20`,
            border: `1px solid ${COLORS.status.error}`,
            borderRadius: '0.5rem',
            color: COLORS.status.error,
            fontSize: '0.875rem',
          }}>
            {error}
          </div>
        )}
        
        <button
          type="submit"
          style={{
            width: '100%',
            padding: '0.75rem',
            background: COLORS.status.info,
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            fontSize: '1rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          Log ind
        </button>
      </form>
    </div>
  );
}

/**
 * Validation Modal Component
 */
function ValidationModal({ result, onClose }) {
  if (!result) return null;
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        background: COLORS.ui.surface,
        borderRadius: '1rem',
        padding: '2rem',
        maxWidth: '800px',
        maxHeight: '80vh',
        overflow: 'auto',
        border: `2px solid ${result.isValid ? COLORS.status.success : COLORS.status.error}`,
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
        }}>
          <h2 style={{ 
            color: result.isValid ? COLORS.status.success : COLORS.status.error,
            margin: 0,
          }}>
            Simuleringsvalidering {result.isValid ? '✅' : '❌'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: COLORS.ui.text.secondary,
              fontSize: '1.5rem',
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>
        
        {/* Validation Summary */}
        <div style={{
          background: COLORS.ui.background,
          padding: '1rem',
          borderRadius: '0.5rem',
          marginBottom: '1.5rem',
        }}>
          <div style={{ display: 'flex', gap: '2rem', marginBottom: '1rem' }}>
            <div>
              <span style={{ color: COLORS.ui.text.muted }}>Kritiske fejl: </span>
              <span style={{ 
                color: result.violations.critical > 0 ? COLORS.status.error : COLORS.status.success,
                fontWeight: 'bold',
                fontSize: '1.25rem',
              }}>
                {result.violations.critical}
              </span>
            </div>
            <div>
              <span style={{ color: COLORS.ui.text.muted }}>Advarsler: </span>
              <span style={{ 
                color: result.violations.warnings > 0 ? COLORS.status.warning : COLORS.status.success,
                fontWeight: 'bold',
                fontSize: '1.25rem',
              }}>
                {result.violations.warnings}
              </span>
            </div>
          </div>
        </div>
        
        {/* Key Metrics */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '1rem',
          marginBottom: '1.5rem',
        }}>
          <div style={{
            padding: '1rem',
            background: COLORS.ui.background,
            borderRadius: '0.5rem',
            borderLeft: `3px solid ${COLORS.status.info}`,
          }}>
            <div style={{ color: COLORS.ui.text.muted, fontSize: '0.875rem' }}>
              Batteri cykler
            </div>
            <div style={{ color: COLORS.ui.text.primary, fontSize: '1.5rem', fontWeight: 'bold' }}>
              {result.metrics?.batteryCycles || 0} / 366
            </div>
          </div>
          
          <div style={{
            padding: '1rem',
            background: COLORS.ui.background,
            borderRadius: '0.5rem',
            borderLeft: `3px solid ${COLORS.solar}`,
          }}>
            <div style={{ color: COLORS.ui.text.muted, fontSize: '0.875rem' }}>
              Sol udnyttelse
            </div>
            <div style={{ color: COLORS.ui.text.primary, fontSize: '1.5rem', fontWeight: 'bold' }}>
              {result.metrics?.solarUtilization?.toFixed(1) || 0}%
            </div>
          </div>
          
          <div style={{
            padding: '1rem',
            background: COLORS.ui.background,
            borderRadius: '0.5rem',
            borderLeft: `3px solid ${COLORS.waste}`,
          }}>
            <div style={{ color: COLORS.ui.text.muted, fontSize: '0.875rem' }}>
              Overskudsvarme
            </div>
            <div style={{ color: COLORS.ui.text.primary, fontSize: '1.5rem', fontWeight: 'bold' }}>
              {result.metrics?.wasteHeatUtilization?.toFixed(1) || 0}%
            </div>
          </div>
        </div>
        
        <button
          onClick={onClose}
          style={{
            padding: '0.75rem 2rem',
            background: COLORS.status.info,
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontWeight: 'bold',
          }}
        >
          Luk
        </button>
      </div>
    </div>
  );
}

// Wrap App with providers
function AppWithProviders() {
  return (
    <ConfigProvider theme={ANT_THEME} locale={daDA}>
      <ScenarioProvider>
        <App />
      </ScenarioProvider>
    </ConfigProvider>
  );
}

export default AppWithProviders;
